<template>
  <div class="desktop-only">
    <NavPage v-if="!isMapFullscreen" />
    <HeaderPage v-if="!isMapFullscreen" />
  </div>
  <div class="mobile-only mb-3">
    <MobileHeader
      v-if="!isMapFullscreen && isMobile"
      :accepted-order="acceptedOrderDetails"
    />
  </div>
  <div class="order-history-page">
    <div class="container py-4">
      <!-- Header -->
      <h2 class="text-center text-panda mb-4">📦 {{ $t("Completed Orders") }}</h2>

      <!-- Simple Date Filter Section -->
      <div class="card shadow-sm border-0 rounded-4 mb-4">
        <div class="card-body">
          <div class="row g-3 align-items-end">
            <!-- Date Range Filter Only -->
            <div class="col-md-8">
              <div class="row g-2">
                <div class="col-md-4">
                  <label class="form-label fw-semibold">Show Orders For</label>
                  <select
                    class="form-select"
                    v-model="dateFilterType"
                    @change="applyDateFilter"
                  >
                    <option value="today">Today</option>
                    <option value="custom">Custom Date Range</option>
                    <option value="all">All Orders</option>
                  </select>
                </div>

                <!-- Custom Date Range - Show only when selected -->
                <div class="col-md-8" v-if="dateFilterType === 'custom'">
                  <div class="row g-2">
                    <div class="col-6">
                      <label class="form-label">From Date</label>
                      <input
                        type="date"
                        class="form-control"
                        v-model="startDate"
                        :max="endDate || getTodayDate()"
                      />
                    </div>
                    <div class="col-6">
                      <label class="form-label">To Date</label>
                      <input
                        type="date"
                        class="form-control"
                        v-model="endDate"
                        :min="startDate"
                        :max="getTodayDate()"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Apply and Reset Buttons -->
            <div class="col-md-4">
              <div class="d-flex gap-2">
                <button
                  class="btn btn-panda flex-grow-1"
                  @click="fetchOrders(1)"
                  :disabled="dateFilterType === 'custom' && (!startDate || !endDate)"
                >
                  <i class="fas fa-filter me-1"></i>
                  Apply Filter
                </button>
                <button
                  class="btn btn-outline-secondary"
                  @click="resetFilters"
                  :disabled="!hasActiveFilters"
                  title="Reset to Today"
                >
                  <i class="fas fa-redo"></i>
                  Clear
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Filter Summary -->
      <div class="mb-3" v-if="hasActiveFilters">
        <div class="filter-summary">
          <small class="text-muted">Active Filter:</small>
          <span class="ms-2">
            <span class="filter-badge">
              <i class="fas fa-calendar me-1"></i>
              {{ getFilterLabel(dateFilterType) }}
              <span v-if="dateFilterType === 'custom'">
                ({{ formatDisplayDate(startDate) }} - {{ formatDisplayDate(endDate) }})
              </span>
              <button class="btn-close btn-close-sm ms-1" @click="resetFilters"></button>
            </span>
          </span>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-5">
        <div class="spinner-border text-panda" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p class="mt-3">Loading your order history...</p>
      </div>

      <template v-else>
        <!-- Empty State -->
        <div v-if="orders.length === 0" class="text-center py-5">
          <div class="mb-4">
            <i class="fas fa-box-open fa-3x text-muted"></i>
          </div>
          <h4 class="text-muted">{{ $t("No completed orders yet") }}</h4>
          <p class="text-muted">{{ $t("Your completed orders will appear here") }}</p>
          <div v-if="hasActiveFilters" class="mt-3">
            <p class="text-muted">No orders found for the selected date range</p>
            <button class="btn btn-panda" @click="resetFilters">
              Show Today's Orders
            </button>
          </div>
        </div>

        <template v-else>
          <!-- Desktop/Tablet View -->
          <div class="d-none d-md-block">
            <div class="card shadow-sm border-0 rounded-4 mb-4">
              <div class="card-body p-0">
                <div class="table-responsive">
                  <table class="table table-hover align-middle mb-0">
                    <thead class="table-light">
                      <tr>
                        <th scope="col" class="ps-4">{{ $t("shop") }}</th>
                        <th scope="col">{{ $t("order code") }}</th>
                        <th scope="col">{{ $t("amount") }}</th>
                        <th scope="col">{{ $t("delivery fees") }}</th>
                        <th scope="col">{{ $t("tax fees") }}</th>
                        <th scope="col">{{ $t("completed date") }}</th>
                        <th scope="col" class="text-center pe-4">{{ $t("actions") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="order in orders" :key="order.id">
                        <td class="ps-4">
                          <div class="d-flex align-items-center">
                            <img
                              :src="
                                order.order_groups?.[0]?.shop?.image_path
                                  ? `https://api.axefood.org/storage/${order.order_groups[0].shop.image_path}`
                                  : 'https://api.axefood.org/default/profile_default.png'
                              "
                              alt="Shop"
                              class="rounded-circle me-3"
                              style="width: 45px; height: 45px; object-fit: cover"
                            />
                            <div class="ms-3">
                              <p class="fw-bold mb-0">
                                {{
                                  order.order_groups?.[0]?.shop?.name || "Unknown Shop"
                                }}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span class="fw-semibold">#{{ order.order_code }}</span>
                        </td>
                        <td>
                          <span class="fw-bold text-panda"
                            >฿ {{ formatPrice(order.total_price) }}</span
                          >
                        </td>
                        <td>
                          <span>฿ {{ formatPrice(order.calculated_delivery_fee || (order.delivery_fee - 10)) }}</span>
                        </td>
                        <td>
                          <span>฿ {{ formatPrice(order.calculated_tax_fee || 10) }}</span>
                        </td>
                        <td>
                          <span>{{ formatDate(order.created_at) }}</span>
                        </td>
                        <td class="text-center pe-4">
                          <button
                            class="btn btn-sm btn-panda rounded-pill px-3"
                            @click="showOrderDetails(order)"
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Mobile View -->
          <div class="d-md-none">
            <div class="row g-4">
              <div
                class="col-12"
                v-for="order in orders"
                :key="'order-mobile-' + order.id"
              >
                <div class="card shadow-sm border-0 rounded-4">
                  <div class="card-body">
                    <div class="d-flex justify-content-between align-items-start mb-3">
                      <div class="d-flex align-items-center">
                        <img
                          :src="
                            order.order_groups?.[0]?.shop?.image_path
                              ? `https://api.axefood.org/storage/${order.order_groups[0].shop.image_path}`
                              : 'https://api.axefood.org/default/profile_default.png'
                          "
                          alt="Shop"
                          class="rounded-circle me-3"
                          style="width: 45px; height: 45px; object-fit: cover"
                        />
                        <div>
                          <h6 class="mb-0 fw-bold">
                            {{ order.order_groups?.[0]?.shop?.name || "Unknown Shop" }}
                          </h6>
                        </div>
                      </div>
                    </div>

                    <div class="row mb-2">
                      <div class="col-6">
                        <small class="text-muted">Order Code</small>
                        <p class="mb-0 fw-semibold">#{{ order.order_code }}</p>
                      </div>
                      <div class="col-6">
                        <small class="text-muted">Delivery Fee</small>
                        <p class="mb-0">฿ {{ formatPrice(order.calculated_delivery_fee || (order.delivery_fee - 10)) }}</p>
                      </div>
                    </div>

                    <div class="row mb-2">
                      <div class="col-6">
                        <small class="text-muted">Tax Fee</small>
                        <p class="mb-0">฿ {{ formatPrice(order.calculated_tax_fee || 10) }}</p>
                      </div>
                      <div class="col-6">
                        <small class="text-muted">Completed Date</small>
                        <p class="mb-0">{{ formatDate(order.created_at) }}</p>
                      </div>
                    </div>

                    <div
                      class="d-flex justify-content-between align-items-center border-top pt-3"
                    >
                      <div>
                        <small class="text-muted">Total Amount</small>
                        <p class="mb-0 fw-bold text-panda">
                          ฿ {{ formatPrice(order.total_price - order.delivery_fee) }}
                        </p>
                      </div>
                      <button
                        class="btn btn-sm btn-panda rounded-pill px-3"
                        @click="showOrderDetails(order)"
                      >
                        Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </template>
    </div>
  </div>

  <!-- Order Details Modal -->
  <div
    v-if="showDetailModal"
    class="order-detail-modal"
    @click.self="showDetailModal = false"
  >
    <div class="detail-modal-content">
      <div class="modal-header">
        <h3>Order Details - #{{ selectedOrder?.order_code }}</h3>
        <button @click="showDetailModal = false" class="close-modal-btn">×</button>
      </div>

      <div v-if="isLoadingDetail" class="loading-spinner">
        <i class="ri-loader-4-line ri-spin"></i> Loading...
      </div>

      <div v-else-if="selectedOrder" class="detail-content">
        <!-- Order Items -->
        <div class="items-section">
          <h4>Order Items</h4>
          <div
            v-for="(group, groupIndex) in selectedOrder.order_groups"
            :key="groupIndex"
            class="order-group"
          >
            <div v-if="group.shop" class="shop-name-header mb-2">
              <strong>🏪 {{ group.shop.name }}</strong>
            </div>

            <div
              v-for="(item, itemIndex) in group.order_items"
              :key="itemIndex"
              class="food-item"
            >
              <template v-if="item.includes && item.includes.length > 0">
                <template v-if="hasSelectType(item.includes)">
                  <div class="main-item">
                    <div class="food-name">{{ item.food?.name || "Food Item" }}</div>
                    <!-- <div class="food-price">฿ {{ formatPrice(item.price) }}</div>
                    <div class="food-qty">× {{ item.quantity }}</div>
                    <div class="food-subtotal">฿ {{ formatPrice(item.price * item.quantity) }}</div> -->
                  </div>
                  <div
                    v-for="(include, includeIndex) in item.includes"
                    :key="includeIndex"
                    class="include-items"
                  >
                    <div class="include-item">
                      <div class="include-name">{{ include.include_food?.name || "Include Item" }}</div>
                      <div class="include-price">฿ {{ formatPrice(include.price) }}</div>
                      <div class="include-qty">× {{ include.quantity }}</div>
                      <div class="include-subtotal">฿ {{ formatPrice(include.price * include.quantity) }}</div>
                      <div v-if="include.include_food?.check_type === 'select'" class="include-badge">select</div>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="main-item">
                    <div class="food-name">{{ item.food?.name || "Food Item" }}</div>
                    <div class="food-price">฿ {{ formatPrice(item.price) }}</div>
                    <div class="food-qty">× {{ item.quantity }}</div>
                    <div class="food-subtotal">฿ {{ formatPrice(item.price * item.quantity) }}</div>
                  </div>
                  <div
                    v-for="(include, includeIndex) in item.includes"
                    :key="includeIndex"
                    class="include-items"
                  >
                    <div class="include-item">
                      <div class="include-name">└ {{ include.include_food?.name || "Include Item" }}</div>
                      <div class="include-price">฿ {{ formatPrice(include.price) }}</div>
                      <div class="include-qty">× {{ include.quantity }}</div>
                      <div class="include-subtotal">฿ {{ formatPrice(include.price * include.quantity) }}</div>
                    </div>
                  </div>
                </template>
              </template>
              <template v-else>
                <div class="main-item">
                  <div class="food-name">{{ item.food?.name || "Food Item" }}</div>
                  <div class="food-price">฿ {{ formatPrice(item.price) }}</div>
                  <div class="food-qty">× {{ item.quantity }}</div>
                  <div class="food-subtotal">฿ {{ formatPrice(item.price * item.quantity) }}</div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- ✅ Summary - USE BACKEND VALUES ONLY -->
        <div class="summary-section">
          <div class="summary-row">
            <span>Subtotal:</span>
            <span>฿ {{ formatPrice(selectedOrder.total_price - selectedOrder.delivery_fee) }}</span>
          </div>
          <div class="summary-row">
            <span>Delivery Fee:</span>
            <span>฿ {{ formatPrice(selectedOrder.calculated_delivery_fee || (selectedOrder.delivery_fee - 10)) }}</span>
          </div>
          <div class="summary-row">
            <span>Tax Fee:</span>
            <span>฿ {{ formatPrice(selectedOrder.calculated_tax_fee || 10) }}</span>
          </div>
          <div class="summary-row total">
            <span>Total:</span>
            <span>฿ {{ formatPrice(selectedOrder.total_price) }}</span>
          </div>
        </div>

        <!-- Customer & Shop Info -->
        <div class="info-section mt-3 pt-3 border-top">
          <div class="row">
            <div class="col-6">
              <h6>Customer Info</h6>
              <p class="mb-1">{{ selectedOrder.user?.name }}</p>
              <p class="mb-0">{{ selectedOrder.user?.phone }}</p>
            </div>
            <div class="col-6">
              <h6>Shops</h6>
              <div v-for="(group, idx) in selectedOrder.order_groups" :key="idx">
                <p class="mb-1">{{ group.shop?.name }}</p>
                <p class="mb-0 small">{{ group.shop?.phone }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="error-message">Failed to load order details</div>
    </div>
  </div>

  <FooterBarPage></FooterBarPage>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import FooterBarPage from "../layout/FooterBarPage.vue";
import NavPage from "../navigation/NavPage.vue";
import axios from "@/api";
import HeaderPage from "../header/HeaderPage.vue";
import MobileHeader from "../header/MobileHeader.vue";

// Reactive data
const orders = ref([]);
const loading = ref(true);
const selectedOrder = ref(null);
const showDetailModal = ref(false);
const isLoadingDetail = ref(false);

// Filter variables
const dateFilterType = ref("today");
const startDate = ref("");
const endDate = ref("");

const pagination = ref({
  current_page: 1,
  last_page: 1,
  per_page: 10,
  total: 0,
});

// Computed property
const hasActiveFilters = computed(() => {
  return dateFilterType.value !== "today";
});

// Helper function to check if includes have select type
const hasSelectType = (includes) => {
  if (!includes || includes.length === 0) return false;
  return includes.some((inc) => inc.include_food?.check_type === "select");
};

// Clean price function
const cleanPrice = (value) => {
  if (!value && value !== 0) return 0;
  
  let cleaned = String(value).replace(/,/g, '').replace(/\s/g, '');
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    cleaned = parts[0] + '.' + parts.slice(1).join('');
  }
  
  return parseFloat(cleaned) || 0;
};

// Get today's date
const getTodayDate = () => {
  return new Date().toISOString().split("T")[0];
};

// Apply date filter
const applyDateFilter = () => {
  if (dateFilterType.value === "custom") {
    const today = new Date();
    const lastWeek = new Date(today);
    lastWeek.setDate(today.getDate() - 7);

    endDate.value = today.toISOString().split("T")[0];
    startDate.value = lastWeek.toISOString().split("T")[0];
  } else {
    startDate.value = "";
    endDate.value = "";
  }
};

// Reset filters
const resetFilters = () => {
  dateFilterType.value = "today";
  startDate.value = "";
  endDate.value = "";
  fetchOrders(1);
};

// Get filter label
const getFilterLabel = (type) => {
  const labels = {
    today: "Today's Orders",
    custom: "Custom Date Range",
    all: "All Orders",
  };
  return labels[type] || type;
};

// Format display date
const formatDisplayDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB");
};

// Build query parameters
const buildQueryParams = (page = 1) => {
  const params = {
    page,
    per_page: pagination.value.per_page,
  };

  if (dateFilterType.value === "today") {
    const today = getTodayDate();
    params.start_date = today;
    params.end_date = today;
  } else if (dateFilterType.value === "custom" && startDate.value && endDate.value) {
    params.start_date = startDate.value;
    params.end_date = endDate.value;
  }

  return params;
};

// Format price
const formatPrice = (price) => {
  const num = cleanPrice(price);
  if (isNaN(num) || num === 0) return "0.00";
  
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num);
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// ✅ Fetch orders from backend
const fetchOrders = async (page = 1) => {
  try {
    loading.value = true;

    const params = buildQueryParams(page);

    console.log("Fetching orders with params:", params);

    const response = await axios.get(`/deli_order_history`, {
      params,
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    });

    const data = response.data;
    orders.value = data.data || [];

    if (data.pagination) {
      pagination.value = data.pagination;
    }
  } catch (error) {
    console.error("Error fetching orders:", error);

    if (error.response && error.response.status === 401) {
      console.error("Authentication failed. Token might be expired or invalid.");
    }

    orders.value = [];
  } finally {
    loading.value = false;
  }
};

// ✅ Show order details - USE BACKEND VALUES ONLY (NO recalculation)
const showOrderDetails = (order) => {
  // Use backend calculated values directly
  const enhancedOrder = {
    ...order,
    // ✅ Use backend's calculated_subtotal
    calculated_subtotal: order.calculated_subtotal || order.total_price,
    calculated_delivery_fee: order.calculated_delivery_fee || (cleanPrice(order.delivery_fee) - 10),
    calculated_tax_fee: order.calculated_tax_fee || 10,
    // Keep order_groups for display
    order_groups: order.order_groups?.map(group => ({
      ...group,
      order_items: group.order_items?.map(item => ({
        ...item,
        food: item.food ? {
          ...item.food,
          price_with_markup: cleanPrice(item.price)
        } : null,
        includes: item.includes?.map(include => ({
          ...include,
          include_food: include.include_food ? {
            ...include.include_food,
            price_with_markup: cleanPrice(include.price)
          } : null
        }))
      }))
    }))
  };
  
  selectedOrder.value = enhancedOrder;
  showDetailModal.value = true;
};

// Initialize
onMounted(() => {
  fetchOrders();
});
</script>

<style scoped>
.order-history-page {
  background-color: #f8f9fa;
  min-height: 100vh;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  border-radius: 30px;
  padding: 30px;
  margin-top: -20px;
  z-index: 1;
  position: relative;
  padding-bottom: 80px !important;
}

.text-panda {
  color: #f2a93e;
}

.btn-panda {
  background-color: #f2a93e;
  color: white;
  border: none;
}

.btn-panda:hover {
  background-color: #e2941d;
  color: white;
}

.btn-panda:disabled {
  background-color: #f2a93e;
  opacity: 0.5;
  cursor: not-allowed;
}

/* Filter Styles */
.filter-summary {
  font-size: 0.9rem;
  color: #6c757d;
}

.filter-badge {
  background-color: #e9ecef;
  color: #495057;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 0.85rem;
  margin-right: 8px;
  display: inline-flex;
  align-items: center;
}

.filter-badge .btn-close {
  font-size: 0.5rem;
  padding: 0.25rem;
  margin-left: 4px;
}

.table th {
  border-top: none;
  font-weight: 600;
  padding: 1rem 0.5rem;
}

.table td {
  padding: 1rem 0.5rem;
  vertical-align: middle;
}

/* Order Detail Modal Styles */
.order-detail-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 15px;
}

.detail-modal-content {
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  animation: modal-appear 0.3s ease-out;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
  position: sticky;
  top: 0;
  background: white;
  border-radius: 16px 16px 0 0;
}

.modal-header h3 {
  margin: 0;
  color: #333;
  font-size: 18px;
}

.close-modal-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0 8px;
}

.close-modal-btn:hover {
  color: #333;
}

.loading-spinner {
  text-align: center;
  padding: 40px;
  color: #666;
}

.detail-content {
  padding: 20px;
}

.items-section {
  margin-bottom: 20px;
}

.items-section h4 {
  margin: 0 0 15px 0;
  color: #333;
  font-size: 16px;
  border-bottom: 2px solid #ff6b00;
  padding-bottom: 8px;
}

.food-item {
  padding: 8px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 10px;
}

.main-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.food-name {
  flex: 2;
  font-weight: 600;
  color: #333;
}

.food-price,
.include-price {
  flex: 1;
  text-align: right;
  color: #666;
}

.food-qty,
.include-qty {
  flex: 0.5;
  text-align: center;
  color: #666;
}

.food-subtotal,
.include-subtotal {
  flex: 1;
  text-align: right;
  font-weight: 600;
  color: #ff6b00;
}

.include-items {
  width: 100%;
  margin-top: 5px;
}

.include-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 4px 8px;
  background: #ffffff;
  border-radius: 6px;
  margin-bottom: 2px;
  border-left: 3px solid #ff6b00;
}

.include-name {
  flex: 2;
  font-weight: 500;
  color: #555;
}

.include-badge {
  font-size: 0.7rem;
  background: #ff6b00;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  margin-left: 5px;
}

.summary-section {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 12px;
  margin-top: 20px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  color: #666;
}

.summary-row.total {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 2px solid #ddd;
  font-weight: 700;
  color: #333;
  font-size: 16px;
}

.info-section h6 {
  color: #333;
  font-weight: 600;
  margin-bottom: 8px;
}

@keyframes modal-appear {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .order-history-page {
    padding: 15px;
    border-radius: 20px;
    padding-bottom: 100px !important;
  }

  .card-body {
    padding: 1rem;
  }

  .order-history-page {
    padding-bottom: max(100px, env(safe-area-inset-bottom) + 80px) !important;
  }

  .food-item {
    flex-wrap: wrap;
  }

  .food-name {
    flex: 100%;
    margin-bottom: 5px;
  }
}
</style>